import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, Search, MapPin, Phone, Building2,
  Users, Filter, X, Zap
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

import { WILAYA_NEIGHBORS as NEIGHBORS } from '../data/wilayasNeighbors';


const normalize = (w) => (w || '').replace(/^\d+\s*-\s*/, '').trim();

const CABINET_LABELS = {
  cabinet_prive: '🏥 Cabinet privé',
  clinique:      '🏨 Clinique',
  polyclinique:  '🏛️ Polyclinique',
  hopital:       '🏪 Hôpital',
};

const SupplierBuyers = () => {
  const { user }  = useAuth();
  const [buyers,   setBuyers]   = useState([]);
  const [stats,    setStats]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [query,    setQuery]    = useState('');
  const [wilayaFilter, setWilayaFilter] = useState('');
  const [showNeighborsOnly, setShowNeighborsOnly] = useState(false);

  // Wilaya du fournisseur connecté
  const supCity   = normalize(user?.city || user?.wilaya || '');
  const neighbors = NEIGHBORS[supCity] || [];
  const myZone    = [supCity, ...neighbors].filter(Boolean);

  useEffect(() => {
    api.get('/supplier/buyers').then(r => setBuyers(r.data)).catch(() => {});
    api.get('/supplier/buyers/stats').then(r => setStats(r.data)).catch(() => {});
    setLoading(false);
  }, []);

  // Filtrage
  const filtered = useMemo(() => {
    let list = buyers;

    if (showNeighborsOnly && myZone.length > 0) {
      list = list.filter(b => {
        const bw = normalize(b.wilaya);
        return myZone.some(z => z.toLowerCase() === bw.toLowerCase());
      });
    }

    if (wilayaFilter) {
      list = list.filter(b =>
        normalize(b.wilaya).toLowerCase().includes(wilayaFilter.toLowerCase())
      );
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(b =>
        (b.name || '').toLowerCase().includes(q) ||
        (b.cabinet_name || '').toLowerCase().includes(q) ||
        (b.city || '').toLowerCase().includes(q) ||
        (b.commune || '').toLowerCase().includes(q) ||
        (b.address || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [buyers, query, wilayaFilter, showNeighborsOnly, myZone]);

  // Grouper par wilaya
  const byWilaya = useMemo(() => {
    const groups = {};
    filtered.forEach(b => {
      const w = b.wilaya || 'Non renseignée';
      if (!groups[w]) groups[w] = [];
      groups[w].push(b);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const isInMyZone = (w) => myZone.some(z => z.toLowerCase() === normalize(w).toLowerCase());
  const isSameDay  = (w) => {
    const wn = normalize(w).toLowerCase();
    return myZone.some(z => z.toLowerCase() === wn);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
        <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to="/fournisseur" className="hover:text-[#1F4D3F]">Tableau de bord</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26]">Annuaire des dentistes</span>
      </nav>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#1F4D3F]">
            Annuaire des dentistes
          </h1>
          <p className="text-[#7A6F5C] mt-1">
            {loading ? '...' : `${buyers.length} cabinet${buyers.length > 1 ? 's' : ''} inscrits · ${filtered.length} affiché${filtered.length > 1 ? 's' : ''}`}
          </p>
        </div>

        {supCity && (
          <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 text-sm text-orange-700">
            <Zap className="w-4 h-4 shrink-0" />
            <span>
              Votre zone express : <strong>{supCity}</strong>
              {neighbors.length > 0 && ` + ${neighbors.length} wilayas voisines`}
            </span>
          </div>
        )}
      </div>

      {/* Stats par wilaya */}
      {stats.length > 0 && (
        <div className="mb-6 overflow-x-auto">
          <div className="flex gap-2 pb-1">
            {stats.slice(0, 10).map(s => (
              <button
                key={s.wilaya}
                onClick={() => setWilayaFilter(wilayaFilter === normalize(s.wilaya) ? '' : normalize(s.wilaya))}
                className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors
                  ${wilayaFilter === normalize(s.wilaya)
                    ? 'bg-[#1F4D3F] border-[#1F4D3F] text-white'
                    : isSameDay(s.wilaya)
                      ? 'bg-orange-50 border-orange-200 text-orange-700 hover:border-orange-400'
                      : 'bg-white border-[#E8E0D0] text-[#5A5247] hover:border-[#1F4D3F]'}`}>
                {isSameDay(s.wilaya) && <Zap className="w-3 h-3" />}
                <MapPin className="w-3 h-3" />
                <span className="font-medium">{normalize(s.wilaya)}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium
                  ${wilayaFilter === normalize(s.wilaya) ? 'bg-white/20' : 'bg-[#F5F1EA]'}`}>
                  {s.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Barre de recherche + filtres */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A6F5C]" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Nom, cabinet, ville, adresse..."
            className="w-full pl-9 pr-8 h-10 border border-[#E8E0D0] rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D3F]"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="w-3.5 h-3.5 text-[#7A6F5C]" />
            </button>
          )}
        </div>

        {supCity && (
          <button
            onClick={() => setShowNeighborsOnly(s => !s)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors
              ${showNeighborsOnly
                ? 'bg-orange-500 border-orange-500 text-white'
                : 'bg-white border-[#E8E0D0] text-[#5A5247] hover:border-orange-400'}`}>
            <Zap className="w-4 h-4" />
            Ma zone express uniquement
          </button>
        )}

        {(wilayaFilter || showNeighborsOnly || query) && (
          <button
            onClick={() => { setWilayaFilter(''); setShowNeighborsOnly(false); setQuery(''); }}
            className="flex items-center gap-1 text-sm text-[#C75B27] hover:underline px-2">
            <X className="w-3.5 h-3.5" /> Effacer les filtres
          </button>
        )}
      </div>

      {/* Liste par wilaya */}
      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-white border border-[#E8E0D0] rounded-2xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-12 text-center">
          <Users className="w-10 h-10 text-[#C9BFAE] mx-auto mb-3" />
          <p className="text-[#5A5247] font-medium">Aucun dentiste trouvé.</p>
          {(query || wilayaFilter || showNeighborsOnly) && (
            <button onClick={() => { setQuery(''); setWilayaFilter(''); setShowNeighborsOnly(false); }}
              className="mt-3 text-sm text-[#1F4D3F] hover:underline">
              Effacer les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {byWilaya.map(([wilaya, list]) => {
            const inZone    = isInMyZone(wilaya);
            const sameDayOk = isSameDay(wilaya);
            return (
              <div key={wilaya}>
                {/* Header wilaya */}
                <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl mb-3
                  ${sameDayOk ? 'bg-orange-50 border border-orange-200' : 'bg-[#F5F1EA] border border-[#E8E0D0]'}`}>
                  {sameDayOk
                    ? <Zap className="w-4 h-4 text-orange-500" />
                    : <MapPin className="w-4 h-4 text-[#1F4D3F]" />
                  }
                  <span className={`font-medium text-sm ${sameDayOk ? 'text-orange-700' : 'text-[#1F4D3F]'}`}>
                    {normalize(wilaya) || 'Wilaya non renseignée'}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                    ${sameDayOk ? 'bg-orange-100 text-orange-600' : 'bg-white text-[#7A6F5C]'}`}>
                    {list.length} cabinet{list.length > 1 ? 's' : ''}
                  </span>
                  {sameDayOk && (
                    <span className="text-xs text-orange-600 font-medium ml-auto">⚡ Zone express</span>
                  )}
                </div>

                {/* Cartes dentistes */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map(b => (
                    <div key={b.user_id}
                      className={`bg-white border rounded-2xl p-4 flex flex-col gap-3
                        ${sameDayOk ? 'border-orange-200' : 'border-[#E8E0D0]'}`}>

                      {/* Header carte */}
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0
                          ${sameDayOk ? 'bg-orange-50' : 'bg-[#F5F1EA]'}`}>
                          {CABINET_LABELS[b.cabinet_type]?.charAt(0) || '🏥'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#2D2A26] truncate">{b.name}</div>
                          {b.cabinet_name && (
                            <div className="text-xs text-[#7A6F5C] truncate">{b.cabinet_name}</div>
                          )}
                          {b.cabinet_type && (
                            <div className="text-xs text-[#C75B27] mt-0.5">
                              {CABINET_LABELS[b.cabinet_type] || b.cabinet_type}
                            </div>
                          )}
                        </div>
                        {sameDayOk && (
                          <span title="Zone express disponible">
                            <Zap className="w-4 h-4 text-orange-400 shrink-0" />
                          </span>
                        )}
                      </div>

                      {/* Adresse complète */}
                      <div className="space-y-1.5 text-sm">
                        {b.address && (
                          <div className="flex items-start gap-2 text-[#5A5247]">
                            <MapPin className="w-3.5 h-3.5 text-[#1F4D3F] mt-0.5 shrink-0" />
                            <span className="leading-tight">
                              {b.address}
                              {b.commune ? `, ${b.commune}` : ''}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-[#5A5247]">
                          <Building2 className="w-3.5 h-3.5 text-[#1F4D3F] shrink-0" />
                          <span>
                            {b.city || normalize(b.wilaya)}
                            {b.commune && b.city && b.commune !== b.city ? '' : ''}
                          </span>
                        </div>
                        {b.phone && (
                          <a href={`tel:${b.phone}`}
                            className="flex items-center gap-2 text-[#1F4D3F] hover:underline">
                            <Phone className="w-3.5 h-3.5 shrink-0" />
                            <span>{b.phone}</span>
                          </a>
                        )}
                      </div>

                      {/* Badge livraison */}
                      {sameDayOk && (
                        <div className="flex items-center gap-1.5 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-1.5">
                          <Zap className="w-3 h-3 shrink-0" />
                          Livraison le jour même possible
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SupplierBuyers;
